import { Box, Button, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MainBorderRadius, TableBgcolor, TableHeader } from '../../../app/AppStyles';
import { SearchKeyFilter } from '../../../app/AppUtils';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../../subcomponents/alertSlice';
import { fetchAllTimetableLinks, fetchTimetablesToLink, putTimetableLink, resetItemsToLink, resetTimetableLinkStatus, setFailedTimetableLink } from './linkTimetableSlice';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkTimetableAddCard from './LinkTimetableAddCard';

export default function LinkTimetableTab() {
    const dispatch = useDispatch();

    const [openNew, setOpenNew] = useState(false);
    const [filter, setFilter] = useState("");

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { activeDepartment } = useSelector((state) => state.settings.theme);
    const { user } = useSelector((state) => state.user);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { timetableLinks, timetableLinksStatus, putTimetableLinkStatus } = useSelector((state) => state.management.linkTimetable);

    useEffect(() => {
        const departmentId = siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null;

        dispatch(fetchAllTimetableLinks(departmentId));
    }, []);

    useEffect(() => {
        if (putTimetableLinkStatus === "success") {
            const departmentId = siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : null;

            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(fetchAllTimetableLinks(departmentId));
            dispatch(resetTimetableLinkStatus());
        } else if (putTimetableLinkStatus === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetTimetableLinkStatus());
        };
    }, [putTimetableLinkStatus]);

    const handleFilterResult = (item) => {
        return SearchKeyFilter(item, ["timetable.name", "linkedItem.linkedName"], filter);
    }

    const newClick = () => {
        dispatch(setFailedTimetableLink(false));
        dispatch(fetchTimetablesToLink());
        setOpenNew(true);
    };

    const callbackModalNew = () => {
        setOpenNew(false);
        setFilter("");
        dispatch(resetItemsToLink());
        dispatch(resetTimetableLinkStatus());
        dispatch(setFailedTimetableLink(false));
    };

    const TableFragment = () => {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - timetableLinks?.filter(handleFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const deleteClick = (item) => {
            var tempitem = { ...item }
            tempitem.createdBy = `AP/${user.number}`;
            dispatch(putTimetableLink(tempitem));
        };

        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? timetableLinks?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : timetableLinks?.filter(handleFilterResult)).map((item, index) =>
                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell align="left">{item?.timetable?.name}</TableCell>
                            {siomSettings?.useMultipleDepartments && <TableCell align="center">{item?.department?.name}</TableCell>}
                            <TableCell align="center">{item?.linkedItem?.linkedName}</TableCell>
                            <TableCell align="center">{item?.linkType}</TableCell>
                            <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton={true} icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${item?.timetable?.name} ${resourceFinal[appLanguage].words.LINK.toLowerCase()} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => deleteClick(item)} />
                            </TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? timetableLinks?.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : timetableLinks?.filter(handleFilterResult)).length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[5, 10, 25]}
                            count={timetableLinks?.filter(handleFilterResult)?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };
    return (
        <>
            <ContentLoader
                successCondition={timetableLinksStatus === "success"}
                errorCondition={timetableLinksStatus === "failed"}
                content={
                    <Stack sx={{ display: 'flex', flex: '1 1 auto', bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                            <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                            <Button variant="outlined" onClick={newClick} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.TIMETABLE}{resourceFinal[appLanguage].words.NAME.toLowerCase()}</TableCell>
                                        {siomSettings?.useMultipleDepartments && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DEPARTMENT}</TableCell>}
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.LINKED}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.LINK}{resourceFinal[appLanguage].words.TYPE.toLowerCase()}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    </Stack>
                }
            />

            <Modal open={openNew} onClose={callbackModalNew}>
                <>
                    <LinkTimetableAddCard handleClose={callbackModalNew} />
                </>
            </Modal>
        </>
    );
}
